import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import API from '../../../../services/API'
import BigModal from '../../../../components/Modals/BigModal'

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null

// Calendar apps sending the request identify themselves in the user agent —
// enough to tell "Google" from "an iPhone" without storing anything else
const describeClient = (userAgent = '') => {
  if (/google/i.test(userAgent)) return 'Google Calendar'
  if (/microsoft|outlook|exchange/i.test(userAgent)) return 'Outlook'
  if (/ios|iphone|ipad|mac ?os|darwin|dataaccessd|calendaragent/i.test(userAgent))
    return 'Apple Calendar'
  return userAgent ? 'a calendar app' : null
}

const subscribeLinks = (url, name) => {
  const webcal = url.replace(/^https?:\/\//, 'webcal://')
  return [
    {
      label: 'Google Calendar',
      href: `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcal)}`,
    },
    {
      label: 'Outlook.com',
      href: `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(url)}&name=${encodeURIComponent(name)}`,
    },
    {
      label: 'Microsoft 365',
      href: `https://outlook.office.com/calendar/0/addfromweb?url=${encodeURIComponent(url)}&name=${encodeURIComponent(name)}`,
    },
    { label: 'Apple Calendar', href: webcal },
  ]
}

const NewLinkPanel = ({ url, name }) => {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      setCopied(false)
    }
  }

  return (
    <Stack gap={1.5}>
      <Alert severity="warning">
        Copy this link now. For your security it won't be shown again. If you
        lose it, reset the link to get a new one.
      </Alert>
      <TextField
        size="small"
        value={url}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                <IconButton size="small" onClick={copy} edge="end">
                  {copied ? (
                    <CheckRoundedIcon fontSize="small" color="success" />
                  ) : (
                    <ContentCopyRoundedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        onFocus={(e) => e.target.select()}
      />
      <Typography variant="body2" fontWeight={700}>
        Add it to your calendar
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {subscribeLinks(url, name).map((link) => (
          <Button
            key={link.label}
            size="small"
            variant="outlined"
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link.label}
          </Button>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Any other calendar app: choose "subscribe" or "add calendar from URL"
        and paste the link. Google Calendar can take up to a day to pick up
        changes, so always check AmbuNet before you travel.
      </Typography>
    </Stack>
  )
}

const CalendarFeeds = () => {
  const [feeds, setFeeds] = useState([])
  const [maxFeeds, setMaxFeeds] = useState(10)
  const [available, setAvailable] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [addOpen, setAddOpen] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // { url, name, reset } — the one-time link to show after create/reset
  const [revealed, setRevealed] = useState(null)
  const [confirm, setConfirm] = useState(null) // { type: 'reset'|'remove', feed }

  const loadFeeds = () => {
    API.get('/account/calendar-feeds').then((res) => {
      setLoading(false)
      if (res.status === 200) {
        setFeeds(res.data.feeds || [])
        setMaxFeeds(res.data.maxFeeds || 10)
        setAvailable(res.data.available !== false)
        setLoadError(false)
      } else {
        setLoadError(true)
      }
    })
  }

  useEffect(loadFeeds, [])

  const closeAdd = () => {
    setAddOpen(false)
    setName('')
    setError('')
    setRevealed(null)
  }

  const handleCreate = async () => {
    if (!name.trim() || busy) return
    setBusy(true)
    setError('')
    const res = await API.post('/account/calendar-feeds', { name: name.trim() })
    setBusy(false)
    if (res.status === 200) {
      setRevealed({ url: res.data.url, name: res.data.feed?.name || name.trim() })
      loadFeeds()
    } else {
      setError(res.data?.error || 'Could not create the calendar link. Please try again.')
    }
  }

  const handleConfirm = async () => {
    if (!confirm || busy) return
    const { type, feed } = confirm
    setBusy(true)
    setError('')
    const res =
      type === 'reset'
        ? await API.post(`/account/calendar-feeds/${feed._id}/reset`)
        : await API.delete(`/account/calendar-feeds/${feed._id}`)
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.error || 'Something went wrong. Please try again.')
      return
    }
    setConfirm(null)
    loadFeeds()
    if (type === 'reset') {
      setRevealed({ url: res.data.url, name: feed.name, reset: true })
      setAddOpen(true)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  return (
    <Stack gap={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={800}>
          Shift calendar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Subscribe to your shifts in Google Calendar, Outlook or Apple
          Calendar. New, moved and cancelled shifts update by themselves.
        </Typography>
      </Box>

      <Alert severity="info" icon={<EventRepeatRoundedIcon fontSize="inherit" />}>
        The calendar shows your shift times, role and where you're working,
        and nothing else: no patient details, colleagues or pay. Anyone with
        the link can see your shifts, so don't share it. Create a separate
        link for each calendar or device so you can remove one on its own.
      </Alert>

      {loadError && (
        <Alert severity="error">
          Couldn't load your calendar links. Refresh the page to try again.
        </Alert>
      )}

      {!available && (
        <Alert severity="warning">
          Calendar links are only available to staff with an employee record.
        </Alert>
      )}

      {feeds.length > 0 && (
        <List dense disablePadding>
          {feeds.map((feed) => {
            const client = describeClient(feed.lastAccessedUserAgent)
            return (
              <ListItem
                key={feed._id}
                divider
                secondaryAction={
                  <Stack direction="row" gap={0.5}>
                    <Tooltip title="Reset link">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setError('')
                          setConfirm({ type: 'reset', feed })
                        }}
                      >
                        <RestartAltRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove link">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => {
                          setError('')
                          setConfirm({ type: 'remove', feed })
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
                sx={{ pr: 11 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <EventRepeatRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={feed.name}
                  secondary={[
                    `Created ${formatDateTime(feed.tokenResetAt || feed.createdAt)}`,
                    feed.lastAccessedAt
                      ? `Last checked ${formatDateTime(feed.lastAccessedAt)}${client ? ` by ${client}` : ''}`
                      : 'Not used yet',
                  ].join(' · ')}
                />
              </ListItem>
            )
          })}
        </List>
      )}

      {feeds.length === 0 && available && !loadError && (
        <Typography variant="body2" color="text.secondary">
          You haven't added your shifts to a calendar yet.
        </Typography>
      )}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        disabled={!available || feeds.length >= maxFeeds}
        onClick={() => {
          setError('')
          setRevealed(null)
          setAddOpen(true)
        }}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add to a calendar
      </Button>
      {feeds.length >= maxFeeds && (
        <Typography variant="caption" color="text.secondary">
          You've reached the limit of {maxFeeds} calendar links. Remove one you
          no longer use to add another.
        </Typography>
      )}

      <BigModal autoWidth open={addOpen} onClose={closeAdd}>
        <Stack padding={1} gap={2} sx={{ width: { sm: 480 }, maxWidth: '100%' }}>
          <Typography variant="title3">
            {revealed?.reset ? `New link for ${revealed.name}` : 'Add your shifts to a calendar'}
          </Typography>
          {revealed ? (
            <>
              {revealed.reset && (
                <Typography variant="body2">
                  The old link has stopped working. Remove the old
                  subscription from your calendar app, then add this one.
                </Typography>
              )}
              <NewLinkPanel url={revealed.url} name={revealed.name} />
              <Button variant="contained" onClick={closeAdd}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2">
                Name this link after the calendar or device you'll add it to,
                so you can tell your links apart later.
              </Typography>
              <TextField
                label="Calendar name"
                size="small"
                value={name}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleCreate()
                  }
                }}
                placeholder="e.g. My iPhone, Work Outlook"
                inputProps={{ maxLength: 60 }}
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button
                variant="contained"
                onClick={handleCreate}
                disabled={busy || !name.trim()}
              >
                {busy ? 'Creating…' : 'Create link'}
              </Button>
            </>
          )}
        </Stack>
      </BigModal>

      <BigModal autoWidth open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <Stack padding={1} gap={2} sx={{ width: { sm: 420 }, maxWidth: '100%' }}>
          <Typography variant="title3">
            {confirm?.type === 'reset'
              ? `Reset "${confirm?.feed?.name}"?`
              : `Remove "${confirm?.feed?.name}"?`}
          </Typography>
          <Typography variant="body2">
            {confirm?.type === 'reset'
              ? "The current link stops working straight away and you'll get a new one to add to your calendar. Use this if the link may have been shared."
              : 'The link stops working straight away. Your calendar app will stop updating and may keep showing old shifts until you remove the subscription there too.'}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button onClick={() => setConfirm(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={confirm?.type === 'reset' ? 'primary' : 'error'}
              onClick={handleConfirm}
              disabled={busy}
            >
              {confirm?.type === 'reset' ? 'Reset link' : 'Remove link'}
            </Button>
          </Stack>
        </Stack>
      </BigModal>
    </Stack>
  )
}

export default CalendarFeeds
